/**
 * «Спросить ИИ» — живой разговор о магазине (просьба заказчика 15.10.2026).
 *
 * S-41 — текущий разговор (последний или новый), S-42 — прошлые разговоры, S-43 — разговор из
 * списка. ИИ видит то же, что человек: отчёты агентов, цифры разделов, товары, рекламу, отзывы,
 * предложения и сигналы (сервер: chat/tools.ts). Ответ приходит потоком: сначала — что ИИ
 * смотрит, затем текст. Числа ответа, которых нет в данных Штаба, сервер помечает — экран
 * показывает их под ответом как оценку ИИ.
 */
import { Fragment, useEffect, useRef, useState } from 'react'
import { chatSend, type ChatEvent } from '../api/client.js'
import {
  fetchChatThread, fetchChatThreads, archiveChatThread, fetchChatFiles, uploadChatFile, deleteChatFile,
  type ChatMessageView, type ChatThread, type ChatFile,
} from '../api/sections.js'
import { hapticSuccess, confirmAction } from '../telegram/call.js'
import { viewerHas, type ScreenId } from '../router/screens.js'
import { Icon } from '../components/icons/Icon.js'
import { Empty, Failed, Loading, NoShop, useAnnounce, useData, useRole, useScreenHead } from './common.js'

type Open = (id: ScreenId, param?: string) => void

// ── Текст ответа: абзацы, списки «- », **жирное». Без HTML — только узлы React. ──

function inline(s: string): React.ReactNode[] {
  return s.split(/(\*\*[^*]+\*\*)/g).map((p, k) =>
    p.startsWith('**') && p.endsWith('**') && p.length > 4 ? <b key={k}>{p.slice(2, -2)}</b> : <Fragment key={k}>{p}</Fragment>)
}

export function Rich({ text }: { text: string }): React.JSX.Element {
  const blocks = text.replace(/\r/g, '').split(/\n{2,}/).map((b) => b.trim()).filter((b) => b !== '')
  return (
    <>
      {blocks.map((b, k) => {
        // Абзац и следом строки «- …» в одном блоке — абзац, затем список (модель так пишет часто).
        const groups: Array<{ list: boolean; lines: string[] }> = []
        for (const l of b.split('\n')) {
          const list = /^\s*[-•]\s+/.test(l)
          const last = groups.at(-1)
          if (last !== undefined && last.list === list) last.lines.push(l); else groups.push({ list, lines: [l] })
        }
        return <Fragment key={k}>{groups.map((g, j) => g.list
          ? <ul className="chat-list" key={j}>{g.lines.map((l, i) => <li key={i}>{inline(l.replace(/^\s*[-•]\s+/, ''))}</li>)}</ul>
          : <p key={j}>{g.lines.map((l, i) => <Fragment key={i}>{i > 0 && <br />}{inline(l.replace(/^#+\s*/, ''))}</Fragment>)}</p>)}</Fragment>
      })}
    </>
  )
}

function Bubble({ m }: { m: Pick<ChatMessageView, 'role' | 'content' | 'tools' | 'ungrounded' | 'failed'> & { at?: string } }): React.JSX.Element {
  if (m.role === 'user') return <div className="chat-msg chat-user"><p>{m.content}</p></div>
  return (
    <div className="chat-msg chat-ai">
      <div className="chat-text"><Rich text={m.content} /></div>
      {m.ungrounded.length > 0 && (
        <p className="chat-note" data-testid="chat-ungrounded">Числа не из данных Штаба — оценка или расчёт ИИ: {m.ungrounded.join(', ')}. Проверьте перед решением.</p>
      )}
      {m.tools.length > 0 && <p className="chat-meta">Смотрел: {m.tools.join(', ')}</p>}
      {m.failed && <p className="chat-meta">Ответ не дописан — спросите ещё раз.</p>}
    </div>
  )
}

/** Подсказки для первого вопроса — только про открытые человеку разделы. */
function suggestions(role: 'owner' | 'staff'): string[] {
  return [
    'Что сейчас главное в магазине?',
    ...(viewerHas(role, 'see.money') ? ['Почему выплата за неделю изменилась?', 'Какие товары приносят больше всего денег?'] : []),
    ...(viewerHas(role, 'see.stock') ? ['Какие товары пора пополнить и сколько?'] : []),
    ...(viewerHas(role, 'see.ads') ? ['Где реклама тратит деньги без заказов?'] : []),
    ...(viewerHas(role, 'see.feedback') ? ['На что чаще всего жалуются покупатели?'] : []),
  ]
}

interface Live { question: string; status: string[]; text: string }

/** Разговор: сообщения, поток ответа, поле вопроса. threadId === null — новый разговор. */
function ChatView({ initial, threadId, onThread, left: left0, maxChars = 2000 }: {
  initial: ChatMessageView[]; threadId: string | null; onThread?: (id: string) => void; left: number; maxChars?: number
}): React.JSX.Element {
  const role = useRole()
  const announce = useAnnounce()
  const [msgs, setMsgs] = useState<ChatMessageView[]>(initial)
  const [thread, setThread] = useState<string | null>(threadId)
  const [live, setLive] = useState<Live | null>(null)
  const [draft, setDraft] = useState('')
  const [left, setLeft] = useState(left0)
  const end = useRef<HTMLDivElement>(null)
  const area = useRef<HTMLTextAreaElement>(null)
  const ctrl = useRef<AbortController | null>(null)
  useEffect(() => () => ctrl.current?.abort(), [])
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }) }, [msgs.length, live?.text, live?.status.length])

  const ask = async (q: string): Promise<void> => {
    const text = q.trim()
    if (text === '' || live !== null) return
    setDraft('')
    setLive({ question: text, status: [], text: '' })
    const c = new AbortController(); ctrl.current = c
    let cur: string | null = thread
    await chatSend({ thread_id: cur, text }, (e: ChatEvent) => {
      if (e.type === 'thread') { cur = e.id; setThread(e.id); onThread?.(e.id) }
      else if (e.type === 'status') setLive((l) => (l === null ? l : { ...l, status: [...l.status, e.text] }))
      else if (e.type === 'delta') setLive((l) => (l === null ? l : { ...l, text: l.text + e.text }))
      else if (e.type === 'done') {
        hapticSuccess()
        setLeft(e.left)
        setMsgs((m) => [...m,
          { id: 'q-' + e.message.id, role: 'user', content: text, tools: [], ungrounded: [], at: '', failed: false },
          { id: e.message.id, role: 'assistant', content: e.message.content, tools: e.message.tools, ungrounded: e.message.ungrounded, at: '', failed: false }])
        setLive(null)
      } else if (e.type === 'error') {
        announce({ kind: 'fail', text: e.message })
        setDraft(text)
        setLive(null)
      }
    }, c.signal)
    // Поток кончился без «готово» и без ошибки — не держим экран в ожидании.
    setLive((l) => { if (l !== null) setDraft(text); return null })
  }

  const empty = msgs.length === 0 && live === null
  return (
    <div className="chat" data-testid="screen-body">
      {empty && (
        <section className="card card-pad chat-intro">
          <p className="body-text">Спросите о магазине что угодно: почему изменилась выплата, что пополнить, где реклама тратит впустую, проверьте гипотезу. ИИ читает отчёты агентов и все цифры Штаба, а числа в ответе берёт из данных.</p>
          <div className="chips chat-sugg">
            {suggestions(role).map((s) => <button type="button" className="seg chat-chip" key={s} data-testid="chat-sugg" onClick={() => { void ask(s) }}>{s}</button>)}
          </div>
        </section>
      )}
      <div className="chat-log" role="log" aria-live="polite" aria-label="Разговор с ИИ">
        {msgs.map((m) => <Bubble key={m.id} m={m} />)}
        {live !== null && <>
          <div className="chat-msg chat-user"><p>{live.question}</p></div>
          <div className="chat-msg chat-ai" data-testid="chat-live">
            {live.status.length > 0 && <p className="chat-meta">{live.status.at(-1)}</p>}
            {live.text !== '' ? <div className="chat-text"><Rich text={live.text} /></div>
              : <p className="chat-typing" aria-label="ИИ отвечает"><span /><span /><span /></p>}
          </div>
        </>}
        <div ref={end} />
      </div>
      {/* Прикреплённые таблицы и поле ввода держатся у низа одним блоком: порознь
          список уезжает под липкое поле и перекрывается им. */}
      <div className="chat-dock">
      <Files />
      <form className="chat-input" onSubmit={(e) => { e.preventDefault(); void ask(draft) }}>
        <textarea ref={area} className="inp chat-area" rows={1} maxLength={maxChars} value={draft} placeholder={left > 0 ? 'Ваш вопрос о магазине' : 'Вопросы на сегодня закончились'}
          aria-label="Ваш вопрос о магазине" data-testid="chat-input" disabled={left <= 0}
          onChange={(e) => { setDraft(e.target.value); const t = e.target; t.style.height = 'auto'; t.style.height = Math.min(160, t.scrollHeight) + 'px' }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && window.matchMedia('(pointer: fine)').matches) { e.preventDefault(); void ask(draft) } }} />
        <button type="submit" className="btn chat-send" aria-label="Отправить" data-testid="chat-send" disabled={live !== null || draft.trim() === '' || left <= 0}>
          <Icon name="action-send" size={20} />
        </button>
      </form>
      </div>
      <p className="chat-left">{left > 0 ? `Осталось вопросов на сегодня: ${left}` : 'Вопросы на сегодня закончились — завтра счётчик обнулится.'}</p>
    </div>
  )
}

/**
 * Таблицы, прикреплённые к разговору (просьба админа 27.10.2026): остатки своего склада,
 * закупки, себестоимость партий — всё, чего Wildberries по API не отдаёт вовсе. Файл
 * разбирается на сервере, а ИИ читает из него строки инструментом: целиком в разговор
 * таблица не попадает, иначе одна выгрузка съела бы весь ответ.
 */
const MAX_FILE_MB = 5
const ACCEPT = '.csv,.tsv,.xlsx,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

function FileChip({ f, onRemove }: { f: ChatFile; onRemove: () => void }): React.JSX.Element {
  return (
    <span className="chat-file" data-testid={'chat-file-' + f.id}>
      <Icon name="section-reports" size={16} className="chat-file-ic" />
      <span className="chat-file-main">
        <span className="chat-file-name">{f.filename}</span>
        <span className="chat-file-meta">{f.rows_total} строк · {f.columns.slice(0, 3).join(', ')}{f.columns.length > 3 ? '…' : ''}</span>
      </span>
      <button type="button" className="chat-file-del" aria-label={`Убрать файл ${f.filename}`} data-testid="chat-file-del" onClick={onRemove}>
        <Icon name="action-decline" size={16} />
      </button>
    </span>
  )
}

function Files(): React.JSX.Element {
  const { s, reload } = useData(fetchChatFiles)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const pick = useRef<HTMLInputElement>(null)
  const announce = useAnnounce()
  const items = s.kind === 'ok' && s.data.shop !== null ? s.data.items ?? [] : []

  const send = async (f: File): Promise<void> => {
    setErr(null)
    if (f.size > MAX_FILE_MB * 1024 * 1024) { setErr(`Файл больше ${MAX_FILE_MB} МБ — пришлите выгрузку поменьше.`); return }
    setBusy(true)
    const b64 = await new Promise<string | null>((done) => {
      const r = new FileReader()
      // readAsDataURL отдаёт «data:…;base64,XXX» — серверу нужна только часть после запятой.
      r.onload = () => done(String(r.result ?? '').split(',')[1] ?? null)
      r.onerror = () => done(null)
      r.readAsDataURL(f)
    })
    if (b64 === null) { setBusy(false); setErr('Не получилось прочитать файл на телефоне.'); return }
    const r = await uploadChatFile(f.name, b64)
    setBusy(false)
    if (r.ok) { announce({ kind: 'ok', text: `Таблица «${r.data.item.filename}» прикреплена: ${r.data.item.rows_total} строк` }); reload() }
    else setErr(r.error.message)
  }

  const remove = async (f: ChatFile): Promise<void> => {
    if (!await confirmAction(`Убрать «${f.filename}» из разговора?`)) return
    const r = await deleteChatFile(f.id)
    if (r.ok) reload(); else announce({ kind: 'fail', text: r.error.message })
  }

  return (
    <div className="chat-files" data-testid="chat-files">
      {items.map((f) => <FileChip key={f.id} f={f} onRemove={() => { void remove(f) }} />)}
      <button type="button" className="seg chat-attach" disabled={busy} data-testid="chat-attach" onClick={() => pick.current?.click()}>
        <Icon name={busy ? 'action-busy' : 'action-new'} size={16} />
        {busy ? 'Читаю таблицу…' : items.length === 0 ? 'Прикрепить таблицу' : 'Ещё таблицу'}
      </button>
      <input ref={pick} type="file" accept={ACCEPT} className="vh" data-testid="chat-file-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f !== undefined) void send(f) }} />
      {err !== null && <p className="chat-file-err" role="alert" data-testid="chat-file-err">{err}</p>}
      {items.length === 0 && err === null && (
        <p className="chat-file-hint">CSV или Excel — то, чего нет в Wildberries: остатки своего склада, закупки, себестоимость.</p>
      )}
    </div>
  )
}

// ── S-41 Спросить ИИ: последний разговор или новый ──────────────────────────

export function ScreenAsk({ open }: { open: Open }): React.JSX.Element {
  const list = useData(fetchChatThreads)
  const latest = list.s.kind === 'ok' && list.s.data.shop !== null ? list.s.data.items[0] ?? null : null
  const thread = useData<ChatThread | null>(async () => (latest === null ? { ok: true as const, data: null } : fetchChatThread(latest.id)), [latest?.id])
  // «Новый разговор»: номер чистого разговора — смена номера пересоздаёт его с пустой лентой.
  const [fresh, setFresh] = useState<number | null>(null)
  const [started, setStarted] = useState(false)
  useScreenHead(undefined, null)
  if (list.s.kind === 'loading' || (latest !== null && fresh === null && thread.s.kind === 'loading')) return <Loading label="Загрузка разговора" rows={3} />
  if (list.s.kind === 'fail') return <Failed message={list.s.message} onRetry={list.reload} />
  const d = list.s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  const t = fresh === null && thread.s.kind === 'ok' ? thread.s.data : null
  // «Новый разговор» — только когда на экране уже есть разговор: пустой экран и так новый.
  const busy = t !== null || started
  return (
    <>
      {(busy || d.items.length > 0) && (
        <div className="chat-bar">
          {busy ? <button type="button" className="btn btn-soft btn-sm" data-testid="chat-new"
            onClick={() => { setStarted(false); setFresh((x) => (x ?? 0) + 1) }}><Icon name="action-new" size={20} />Новый разговор</button> : <span />}
          {d.items.length > 0 && <button type="button" className="link-inline" data-testid="chat-history" onClick={() => open('S-42')}>Прошлые разговоры</button>}
        </div>
      )}
      <ChatView key={t !== null ? t.id : `new-${fresh ?? 0}`} initial={t?.messages ?? []} threadId={t?.id ?? null}
        left={t?.left ?? d.left} maxChars={d.max_chars} onThread={() => setStarted(true)} />
    </>
  )
}

// ── S-42 Прошлые разговоры ───────────────────────────────────────────────────

export function ScreenAskHistory({ open }: { open: Open }): React.JSX.Element {
  const { s, reload } = useData(fetchChatThreads)
  useScreenHead(undefined, null)
  const announce = useAnnounce()
  if (s.kind === 'loading') return <Loading label="Загрузка разговоров" rows={5} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  if (d.items.length === 0) return <Empty icon="tab-ask" title="Разговоров пока нет" text="Задайте первый вопрос во вкладке «Спросить»." testid="empty" />
  return (
    <section className="card" data-testid="screen-body">
      {d.items.map((t) => (
        <div className="row row-tap-wrap" key={t.id}>
          <button type="button" className="row row-tap" data-testid={'thread-' + t.id} onClick={() => open('S-43', t.id)}>
            <span className="row-main">
              <span className="row-title">{t.title}</span>
              <span className="row-meta">{t.at} · вопросов: {t.questions}</span>
            </span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
          <button type="button" className="link-inline chat-del" aria-label={`Убрать разговор «${t.title}»`}
            onClick={() => { void confirmAction('Убрать разговор из списка?').then(async (yes) => {
              if (!yes) return
              const r = await archiveChatThread(t.id)
              if (!r.ok) announce({ kind: 'fail', text: r.error.message }); else { announce({ kind: 'ok', text: 'Разговор убран' }); reload() }
            }) }}>Убрать</button>
        </div>
      ))}
    </section>
  )
}

// ── S-43 Разговор из списка — можно продолжить ───────────────────────────────

export function ScreenAskThread({ id }: { id: string }): React.JSX.Element {
  const { s, reload } = useData(() => fetchChatThread(id), [id])
  useScreenHead(s.kind === 'ok' && s.data.shop !== null ? s.data.title : undefined, null)
  if (s.kind === 'loading') return <Loading label="Загрузка разговора" rows={4} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  return <ChatView key={d.id} initial={d.messages} threadId={d.id} left={d.left} />
}
